import { useEffect, useState } from "react";

// Interfaces
import { TInputData } from "@/Layouts/types/InputComponents/InputFormExtension";
import { IMatrixRowProps } from "./interface";

// Custom components
import DoubleInput from "@/Layouts/components/InputComponents/DoubleInput";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import TierRowsItem from "@/Layouts/components/Tiers/TierRowsItem";

// MUI
import { Stack } from "@mui/material";

export default function MatrixRow(props: Readonly<IMatrixRowProps<TInputData>>) {
	const {
		index = 0,
		item = null,
		fromDisabled = false,
		toDisabled = false,
		spaces = {
			mainSection: 100,
			columns: 100,
			totalColumns: 1,
			totalRows: 1,
			separation: 5.5,
		},
		inputDefinitions = [],
		rowsErrors = [],
		columnsErrors = [],
		lastBonusError = false,
		readOnly = false,
		spacing = 0.8,
		mainContentProps = null,
		sxMainContent = {
			minWidth: "190px",
		},
		cellsProps = {
			justifyContent: "center",
			alignItems: "center",
		},
		sxCells = {
			minWidth: "100px",
		},
		onActionUpdate = () => {},
		onChangeType = () => {},
	} = props;

	const [localInputs, setLocalInputs] = useState<TInputData[]>(inputDefinitions);

	useEffect(() => {
		setLocalInputs([...inputDefinitions]);

		return () => {
			setLocalInputs([]);
		};
	}, [inputDefinitions]);

	const firstInputDefinition = localInputs.map((input) => ({ ...input, dashed: fromDisabled }));
	const secondInputDefinition = localInputs.map((input) => ({ ...input, dashed: toDisabled }));

	return (
		<TierRowsItem hasShadow={!(index % 2)} spaces={spaces}>
			<Stack direction={"row"} spacing={spacing}>
				<Stack {...mainContentProps} sx={sxMainContent}>
					<DoubleInput
						firstInput={{
							data: [...firstInputDefinition],
							sx: { width: "95px" },
							value: item?.from?.value ?? 0,
							defaultType: item?.from?.input ?? "Dollar",
							error: Boolean(rowsErrors?.[index]?.from),
							disabled: fromDisabled,
							readOnly: true,
						}}
						secondInput={{
							readOnly,
							data: [...secondInputDefinition],
							sx: { width: "95px" },
							value: item?.to?.value ?? 0,
							defaultType: item?.to?.input ?? "Dollar",
							error: Boolean(rowsErrors?.[index]?.to),
							disabled: toDisabled,
							onActionUpdate: (value) => {
								onActionUpdate?.(index, 0, "row", "to", value);
							},
							onChangeType: (_, input) => {
								onChangeType?.(index, 0, "row", "to", input);
							},
						}}
					/>
				</Stack>
				<Stack direction={"row"} spacing={spacing}>
					{item?.bonuses?.map((element, key) => {
						const isBonusError =
							(key === item?.bonuses?.length - 1 && lastBonusError) || Boolean(columnsErrors?.[index]?.[key]?.["bonus"]);
						const customKey = `${index}_${key}`;
						return (
							<Stack key={customKey} {...cellsProps} sx={sxCells}>
								<InputFormExtension
									data={[
										{
											type: "Dollar",
											min: -10000,
											max: 1000000,
											decimalsEditable: false,
											decimals: 2,
										},
										{
											type: "Percent",
											min: -100,
											max: 100,
											decimalsEditable: true,
											decimals: 2,
										},
										{
											type: "Dollar per Unit",
											min: -1000,
											max: 1000,
											decimalsEditable: true,
											decimals: 2,
										},
									]}
									value={element?.bonus?.value || 0}
									defaultType={element?.bonus?.input || "Percent"}
									error={isBonusError}
									disabled={readOnly}
									readOnly={readOnly}
									onChangeType={(_, input) => {
										onChangeType?.(index, key, "row", "bonus", input);
									}}
									onActionUpdate={(value) => {
										onActionUpdate?.(index, key, "row", "bonus", value);
									}}
									sx={{
										width: "100px",
									}}
								/>
							</Stack>
						);
					})}
				</Stack>
			</Stack>
		</TierRowsItem>
	);
}
